import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class VendorService {
  private apiUrl = environment.apiUrl + '/vendors';
  private baseUrl = environment.apiUrl;
  constructor(private http: HttpClient) {}

  // Create new vendor
  addVendor(vendorData: any) {
    return this.http.post(this.apiUrl, vendorData);
  }

  /** Code the next new vendor will get (preview; the server assigns it on create). */
  getNextCode() {
    return this.http.get<{ code: string }>(`${this.apiUrl}/next-code`);
  }

  // Get list of vendors with optional filters
  getVendors(params?: any) {
    return this.http.get(this.apiUrl, { params });
  }

  // Get vendor by ID
  getVendorById(id: string) {
    return this.http.get(`${this.apiUrl}/${id}`);
  }

  searchVendors(params?: any) {
    return this.http.get(`${this.apiUrl}/search`, { params });
  }

  // Update vendor by ID
  updateVendor(id: string, vendorData: any) {
    return this.http.patch(`${this.apiUrl}/${id}`, vendorData);
  }

  // Soft delete vendor by ID
  deleteVendor(id: string) {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  getProductsByVendorId(vendorId: string) {
    return this.http.get(`${this.apiUrl}/${vendorId}/products`);
  }

  replaceVendor(body: any) {
    return this.http.patch(
      `${this.baseUrl}/products/vendor-mapping/vendor`,
      body,
    );
  }

  getManufacturerDetails(vendorId: string) {
    return this.http.get(`${this.apiUrl}/${vendorId}/manufacturers`);
  }

  updateVendorStatus(vendorId: string, isActive: boolean): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${vendorId}/status`, { isActive });
  }

  // Download vendor template (Creation)
  downloadTemplate(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/template`, {
      responseType: 'blob',
    });
  }

  // Download vendor update template (Update)
  downloadUpdateTemplate(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/template/update`, {
      responseType: 'blob',
    });
  }

  // Download vendor mapping template (Mapping)
  downloadMappingTemplate(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/template/mapping`, {
      responseType: 'blob',
    });
  }

  // Bulk upload vendors (Creation)
  bulkUpload(formData: FormData): Observable<any> {
    return this.http.post(`${this.apiUrl}/upload`, formData);
  }

  // Bulk update vendors (Update)
  bulkUpdateUpload(formData: FormData): Observable<any> {
    return this.http.post(`${this.apiUrl}/upload/update`, formData);
  }

  // Bulk mapping upload (Mapping)
  bulkMappingUpload(formData: FormData): Observable<any> {
    return this.http.post(`${this.apiUrl}/upload/mapping`, formData);
  }

  validateGstin(gstin: string) {
    return this.http.post(`${this.baseUrl}/gstzen/gstin-validate`, { gstin });
  }

  // Export vendors to Excel
  exportVendors(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/export/excel`, {
      responseType: 'blob',
    });
  }

  // Get all vendors (unpaginated) for dropdowns
  getAllVendorsList(): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.apiUrl}/list/all`)
      .pipe(
        map((vendors) =>
          vendors.map((v) => ({
            id: v.vendorId,
            name: v.name,
            costPriceIncludesGst: v.costPriceIncludesGst,
            // Surfaced for app-search-select's vendor preset (City · GSTIN
            // secondary line). Backend returns these via /vendors/list/all.
            city: v.city,
            gstin: v.gstin,
          })),
        ),
      );
  }

  // ─── Vendor SKU Mapping ────────────────────────────────────────────────────

  downloadSkuMappingTemplate(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/template/sku-mapping`, {
      responseType: 'blob',
    });
  }

  previewSkuMapping(vendorId: string, formData: FormData): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/${vendorId}/sku-mapping/preview`,
      formData,
    );
  }

  uploadSkuMapping(vendorId: string, formData: FormData): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/${vendorId}/sku-mapping/upload`,
      formData,
    );
  }

  getSkuMappings(
    vendorId: string,
    page = 1,
    limit = 10,
    search = '',
  ): Observable<any> {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString(),
    };
    if (search.trim()) params['search'] = search.trim();
    return this.http.get(`${this.apiUrl}/${vendorId}/sku-mapping`, { params });
  }

  // Typeahead for the manual add box — one term matches SKU or name.
  searchProductsForMapping(vendorId: string, q: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrl}/${vendorId}/sku-mapping/product-search`,
      { params: { q } },
    );
  }

  addSkuMapping(
    vendorId: string,
    body: { productId: string; minOrderQty?: number; leadTime?: number },
  ): Observable<any> {
    return this.http.post(`${this.apiUrl}/${vendorId}/sku-mapping`, body);
  }

  exportSkuMappings(vendorId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${vendorId}/sku-mapping/export`, {
      responseType: 'blob',
    });
  }

  removeSkuMapping(vendorId: string, productId: string): Observable<any> {
    return this.http.delete(
      `${this.apiUrl}/${vendorId}/sku-mapping/${productId}`,
    );
  }

  bulkRemoveSkuMappings(
    vendorId: string,
    productIds: string[],
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/${vendorId}/sku-mapping/bulk-remove`,
      { productIds },
    );
  }

  getUnmappedVendorCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(
      `${this.baseUrl}/products/unmapped-vendor-count`,
    );
  }
}
